import { useEffect, useMemo, useState } from 'react';
import { MaterialSku, SKU_PROP_RAW } from '../api/material';
import { parseGroupFields, SchemaGroup, SchemaGroupField, skuSchemaClient } from '../api/schema';

// SKU 规格 (2026-10-14, Schema 驱动) 的模板缓存 / 表单态 / 校验 / 摘要. 组件见 components/SkuSpec.
// 校验口径与后端 models.MaterialSku.CheckSkuSpec 一致: 原材料必须选类型; 选了类型则只认模板字段,
// 必填项非空, 数值字段可解析且不为负, 有选项的只能从中选.

// ---- 模板缓存: SKU 列表 / 弹窗 / 向导都要, 整站一份, 模板管理页保存后失效 ----

let cache: Promise<SchemaGroup[]> | null = null;

export function loadSkuSchemas(): Promise<SchemaGroup[]> {
    if (!cache) {
        cache = skuSchemaClient.list().catch((e) => {
            cache = null; // 失败不缓存, 下次重试
            throw e;
        });
    }
    return cache;
}

export function invalidateSkuSchemas() {
    cache = null;
}

/** 规格模板清单 + 按编码索引 (含停用; 下拉只列启用的, 存量值引用停用类型时仍能显示名称). */
export function useSkuSchemas() {
    const [list, setList] = useState<SchemaGroup[]>([]);
    useEffect(() => {
        let alive = true;
        loadSkuSchemas()
            .then((l) => alive && setList(l))
            .catch(() => alive && setList([]));
        return () => {
            alive = false;
        };
    }, []);
    const byKey = useMemo(() => new Map(list.map((g) => [g.groupKey, g])), [list]);
    return { list, byKey };
}

export function specFieldsOf(g?: SchemaGroup): SchemaGroupField[] {
    return g ? parseGroupFields(g.fields) : [];
}

// ---- 表单态 ----

/** 规格表单态: 类型 + 各字段输入原文 ('' = 未填). */
export interface SkuSpecDraft {
    group: string;
    values: Record<string, string>;
}

export const EMPTY_SKU_SPEC: SkuSpecDraft = { group: '', values: {} };

/** 规格值 JSON → 对象 (坏数据按空). */
export function parseSpecParams(raw?: string): Record<string, unknown> {
    if (!raw) return {};
    try {
        const v = JSON.parse(raw);
        return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
    } catch {
        return {};
    }
}

/** 存量 SKU → 表单态. */
export function specDraftOf(sku?: Partial<MaterialSku> | null): SkuSpecDraft {
    const values: Record<string, string> = {};
    for (const [k, v] of Object.entries(parseSpecParams(sku?.specParams))) {
        if (v !== null && v !== undefined) values[k] = String(v);
    }
    return { group: sku?.specGroup || '', values };
}

/**
 * 提交前校验并转成请求体: 返回 [payload, 错误文案]; 错误非空时不要提交.
 * payload 总是带 specGroup + specParams (整组), 后端按"改后整行"再校验一次.
 */
export function validateSkuSpec(
    prop: string,
    draft: SkuSpecDraft,
    byKey: Map<string, SchemaGroup>,
): [{ specGroup: string; specParams: Record<string, unknown> }, string] {
    const specParams: Record<string, unknown> = {};
    const payload = { specGroup: draft.group, specParams };
    if (!draft.group) {
        return [payload, prop === SKU_PROP_RAW ? '原材料 SKU 必须选择规格类型并填写规格' : ''];
    }
    const g = byKey.get(draft.group);
    if (!g) return [payload, `规格类型不存在: ${draft.group}`];
    if (!g.isEnabled) return [payload, `规格类型已停用: ${g.label}`];
    const missing: string[] = [];
    for (const f of specFieldsOf(g)) {
        const raw = (draft.values[f.key] ?? '').trim();
        if (raw === '') {
            if (f.required) missing.push(f.label);
            continue;
        }
        if (f.options?.length) {
            if (!f.options.includes(raw)) return [payload, `规格 ${f.label} 只能取: ${f.options.join(' / ')}`];
            specParams[f.key] = raw;
        } else if (f.type === 'str') {
            specParams[f.key] = raw;
        } else {
            const n = Number(raw);
            if (!Number.isFinite(n)) return [payload, `规格 ${f.label} 应为数字`];
            if (n < 0) return [payload, `规格 ${f.label} 不能为负数`];
            specParams[f.key] = n;
        }
    }
    if (missing.length) return [payload, `规格类型「${g.label}」必填项未填: ${missing.join(' / ')}`];
    return [payload, ''];
}

// ---- 展示 ----

export interface SkuSpecSummary {
    /** 类型名; '' = 未选 */
    label: string;
    /** "长 300mm · 宽 200mm · 厚度 3mm" (按模板字段顺序, 跳过未填) */
    text: string;
    /** text 拆开的每一项 ("长 300mm"), 列表里逐项不断行 */
    parts: string[];
    /** 原材料未选类型 / 必填项未填 */
    missing: boolean;
}

export function specValueText(f: SchemaGroupField, v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    return `${v}${f.unit || ''}`;
}

export function summarizeSkuSpec(sku: Partial<MaterialSku>, byKey: Map<string, SchemaGroup>): SkuSpecSummary {
    if (!sku.specGroup) return { label: '', text: '', parts: [], missing: sku.prop === SKU_PROP_RAW };
    const g = byKey.get(sku.specGroup);
    const params = parseSpecParams(sku.specParams);
    if (!g) return { label: sku.specGroup, text: '', parts: [], missing: false };
    const parts: string[] = [];
    let missing = false;
    for (const f of specFieldsOf(g)) {
        const t = specValueText(f, params[f.key]);
        if (t) parts.push(`${f.label} ${t}`);
        else if (f.required) missing = true;
    }
    return { label: g.label, text: parts.join(' · '), parts, missing };
}
